import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import makeStyles from "@mui/styles/makeStyles";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import LockOpenOutlinedIcon from "@mui/icons-material/LockOpenOutlined";

import { isB2cCaseMasterUser } from "utils";
import { useNavigateWithClient } from "hooks";

const CASE_MASTER_ROUTE = "/personalized-interview?type=case-master";

// Dismissal is per-session: the banner is a discovery aid, so it should come
// back on the next login rather than disappearing forever after one click.
const DISMISS_KEY = "casemaster-banner-dismissed";

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: theme.palette.success["clr-100"],
    border: "1px solid",
    borderColor: theme.palette.success["clr-300"],
    borderRadius: "4px",
    padding: theme.spacing(3, 5),
    marginBottom: theme.spacing(5),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(3),
  },
  message: {
    flex: 1,
  },
  link: {
    color: theme.palette.primary["clr-300"],
    textDecoration: "underline",
    cursor: "pointer",
  },
  closeBtn: {
    color: theme.palette.success["clr-700"],
    padding: theme.spacing(1),
  },
}));

/**
 * Case Master B2C users land on the portal with no clue where their purchase
 * lives, so point them at the Case Master tab of the Interview Wizard. Rendered
 * above the page header (Navbar) on the dashboard only (opt-in via AppShell's
 * `showCaseMasterBanner`), and hidden for everyone without a Case Master plan.
 */
export default function CaseMasterBanner() {
  const classes = useStyles();
  const navigateWithClient = useNavigateWithClient();

  const [dismissed, setDismissed] = React.useState(
    () => sessionStorage.getItem(DISMISS_KEY) === "true"
  );

  const goToCaseMaster = React.useCallback(() => {
    navigateWithClient(CASE_MASTER_ROUTE);
  }, [navigateWithClient]);

  const handleKeyDown = React.useCallback((event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      goToCaseMaster();
    }
  }, [goToCaseMaster]);

  const handleDismiss = React.useCallback(() => {
    sessionStorage.setItem(DISMISS_KEY, "true");
    setDismissed(true);
  }, []);

  if (dismissed || !isB2cCaseMasterUser()) return null;

  return (
    <Box className={classes.root}>
      <LockOpenOutlinedIcon style={{ color: "#00664A", fontSize: "20px" }} />
      <Typography
        variant="body01-bold"
        color="success.clr-700"
        className={classes.message}
      >
        Casemaster unlocked!&nbsp;
        <Typography
          component="span"
          variant="body01-bold"
          className={classes.link}
          role="button"
          tabIndex={0}
          onClick={goToCaseMaster}
          onKeyDown={handleKeyDown}
        >
          Click here
        </Typography>
        &nbsp;to start practicing cases.
      </Typography>
      <IconButton
        size="small"
        aria-label="Dismiss Case Master banner"
        className={classes.closeBtn}
        onClick={handleDismiss}
      >
        <CloseRoundedIcon fontSize="small" />
      </IconButton>
    </Box>
  );
}
